import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { useEffect, useRef, useState } from "react";
import usePages, { useSelectedPage, useUpdatePageProperties } from "../../../Hooks/Display/usePages";
import { getPageFromPages } from "../utils";
import { allowedComponentTypes, getComponentByProperty } from "./utils";
import { createComponent } from "../../../API/Display/Display";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import useComponents from "../../../Hooks/Display/useComponents";
import { ADD_COMPONENTS, SET_SELECTED_COMPONENT, TOGGLE_ADD_COMPONENT_DIALOG } from "../../../Context/Display/Components/ComponentsContextEvents";
import { ADD_COMPONENT_TO_PAGE } from "../../../Context/Display/Pages/PagesContextEvents";

function NewComponentDialog() {

    // hooks
    const [{ components, newComponentParentId }, dispatch] = useComponents();
    const [, pageDispatch] = usePages();
    const entityName = usePropsStoreValue("entityName");

    // refs
    const createComponentButton = useRef(null);

    // variables
    const selectedPage = useSelectedPage();

    // form fields
    const [componentName, setComponentName] = useState("");
    const [componentType, setComponentType] = useState("");
    const [allowedComponents, setAllowedComponents] = useState([]);

    // form validation
    const [componentWithSameTypeExists, setComponentWithSameTypeExists] = useState(false);
    const [disabledComponentType, setDisabledComponentType] = useState(false);
    const [nameExists, setNameExists] = useState(false);
    const [nameHasSpChars, setNameHasSpChars] = useState(false);

    useEffect(() => {
        let allowedTypes = [];
        if (newComponentParentId === selectedPage._id) {
            if (selectedPage.subPageType === "Single Record") {
                allowedTypes = allowedComponentTypes[selectedPage.subPageType].filter(type => type !== "Tab");
            } else {
                allowedTypes = allowedComponentTypes[selectedPage.subPageType]
            }
        } else {
            const parentComponent = getComponentByProperty(components, "id", newComponentParentId);
            switch (parentComponent.componentType) {
                case "Tab Container":
                    allowedTypes = ["Tab"];
                    break;
                case "Split Section":
                    allowedTypes = ["Stacked Section"];
                    break;
                default:
                    if (selectedPage.subPageType === "Single Record") {
                        allowedTypes = allowedComponentTypes[selectedPage.subPageType].filter(type => type !== "Tab");
                    } else {
                        allowedTypes = allowedComponentTypes[selectedPage.subPageType];
                    }
                    break;
            }
        }
        setAllowedComponents(allowedTypes);
        setComponentName(allowedTypes[0]);
        setComponentType(allowedTypes[0]);
    }, [])

    function handleComponentName(event) {
        setComponentName(event.target.value);
    }

    function handleComponentType(event) {
        setComponentType(event.target.value);
        setComponentName(event.target.value)
    }

    function closeAddComponentDialog() {
        dispatch({
            type: TOGGLE_ADD_COMPONENT_DIALOG
        })
    }

    async function createNewComponent(event) {
        try {
            event.target.disabled = true;
            const properties = {
                componentName, componentType
            };
            const pageId = selectedPage._id || selectedPage.id;
            const component = await createComponent(
                pageId, newComponentParentId, entityName, properties
            )
            dispatch({
                type: ADD_COMPONENTS,
                payload: component
            })
            dispatch({
                type: TOGGLE_ADD_COMPONENT_DIALOG
            })
            pageDispatch({
                type: ADD_COMPONENT_TO_PAGE,
                payload: {
                    parentId: selectedPage.parentId,
                    pageId: selectedPage._id,
                    component: component
                }
            })
        } catch (error) {
            //
        }
    }

    function addComponentToParent(parentId, component, components = selectedPage.components) {
        if (parentId === selectedPage._id) {
            components.push(component);
            return;
        }
        for (let i = 0; i < components.length; i++) {
            if (components[i].id === parentId) {
                if (!components[i].children) {
                    components[i].children = [];
                }
                components[i].children.push(component);
                break;
            }
            addComponentToParent(parentId, component, components[i].components)
        }
    }

    return (
        <Dialog
            title="Create New Component"
            onClose={() => dispatch({ type: TOGGLE_ADD_COMPONENT_DIALOG })}
        >
            <form>
                <div className="form-label d-inline-block me-1" required>
                    Type
                </div>
                {componentWithSameTypeExists && (
                    <span className="text-danger">
                        Component with that type already exist
                    </span>
                )}
                <ComboBox
                    placeholder="Select component type"
                    value={componentType}
                    className="form-control-custom"
                    data={allowedComponents}
                    onChange={handleComponentType}
                    disabled={disabledComponentType}
                />

                <div className="d-flex align-items-center">
                    <div className="form-label" required> Name </div>
                    {nameExists && (
                        <span className="text-danger">
                            Component with that name already exist
                        </span>
                    )}
                    {nameHasSpChars && (
                        <small className="text-danger">
                            Component cannot have speical characters
                        </small>
                    )}
                </div>
                <input
                    maxLength="54"
                    name="Page Name"
                    className="form-control name-text"
                    onChange={handleComponentName}
                    value={componentName}
                />
                <DialogActionsBar layout="end">
                    <button
                        type={'button'}
                        onClick={closeAddComponentDialog}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        autoFocus={true}
                        type="button"
                        onClick={createNewComponent}
                        className="btn btn-primary"
                        disabled={
                            componentName.trim() === "" ||
                            nameExists ||
                            componentWithSameTypeExists
                        }
                        ref={createComponentButton}
                    >
                        Create
                    </button>
                </DialogActionsBar>
            </form>
        </Dialog>
    )
}

export default NewComponentDialog;